import { create } from 'zustand'
import toast from 'react-hot-toast'
import type { Lesson, LessonActivity, Player, PlayerResponse, LessonSession } from '@/types'
import { webSocketService } from '@/services/websocket'
import { audioService } from '@/services/audio'

interface LessonState {
  currentLesson: Lesson | null
  lessonSession: LessonSession | null
  isConnected: boolean
  connectionError: string | null
  players: Player[]
  currentActivity: LessonActivity | null
  currentSlide: number
  /** Tracks the last PDF activity ID so we don't reset the slide when polling
   *  briefly sets currentActivity to null and then back to the same activity. */
  _lastPdfActivityId: string | null
  /** Timestamp until which polling should not overwrite currentActivity.
   *  Used after timer extend to prevent stale poll data from clobbering the update. */
  _suppressActivityPollUntil: number
  responses: PlayerResponse[]
  isLoading: boolean
  error: string | null
  audioEnabled: boolean
}

interface LessonActions {
  setCurrentLesson: (lesson: Lesson | null) => void
  setLessonSession: (session: LessonSession | null) => void
  updateLessonState: (updates: Partial<Lesson>) => void
  addPlayer: (player: Player) => void
  removePlayer: (playerId: string) => void
  updatePlayer: (playerId: string, updates: Partial<Player>) => void
  setPlayers: (players: Player[]) => void
  setCurrentActivity: (activity: LessonActivity | null) => void
  setCurrentSlide: (page: number) => void
  suppressActivityPoll: (durationMs: number) => void
  updateActivityInLesson: (updatedActivity: LessonActivity) => void
  addResponse: (response: PlayerResponse) => void
  setResponses: (responses: PlayerResponse[]) => void
  setConnectionStatus: (connected: boolean, error?: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setAudioEnabled: (enabled: boolean) => void
  connectToLesson: (lessonId: string, userRole?: 'host' | 'player', sessionInfo?: { runCode: string; sessionId: string }) => () => void
  disconnectFromLesson: () => void
  reset: () => void
}

export const useLessonStore = create<LessonState & LessonActions>()((set, get) => ({
  // Initial state
  currentLesson: null,
  lessonSession: null,
  isConnected: false,
  connectionError: null,
  players: [],
  currentActivity: null,
  currentSlide: 1,
  _lastPdfActivityId: null,
  _suppressActivityPollUntil: 0,
  responses: [],
  isLoading: false,
  error: null,
  audioEnabled: localStorage.getItem('audioEnabled') !== 'false',

  // Actions
  setCurrentLesson: (lesson: Lesson | null) => {
    set({ currentLesson: lesson })
  },

  setLessonSession: (session: LessonSession | null) => {
    set({ lessonSession: session })
  },

  updateLessonState: (updates: Partial<Lesson>) => {
    set((state) => ({
      currentLesson: state.currentLesson
        ? { ...state.currentLesson, ...updates }
        : null
    }))
  },

  addPlayer: (player: Player) => {
    set((state) => ({
      players: [...state.players.filter(p => p.id !== player.id), player]
    }))
  },

  removePlayer: (playerId: string) => {
    set((state) => ({
      players: state.players.filter(p => p.id !== playerId)
    }))
  },

  updatePlayer: (playerId: string, updates: Partial<Player>) => {
    set((state) => ({
      players: state.players.map(player =>
        player.id === playerId ? { ...player, ...updates } : player
      )
    }))
  },

  setPlayers: (players: Player[]) => {
    set({ players })
  },

  setCurrentSlide: (page: number) => {
    set({ currentSlide: page })
  },

  suppressActivityPoll: (durationMs: number) => {
    set({ _suppressActivityPollUntil: Date.now() + durationMs })
  },

  setCurrentActivity: (activity: LessonActivity | null) => {
    const lastPdfId = get()._lastPdfActivityId
    // When switching to a GENUINELY NEW pdf_slideshow activity, sync currentSlide
    // from activity_data.  We compare against _lastPdfActivityId (not just
    // previousActivity.id) so that a brief null gap from polling doesn't trick
    // us into resetting the slide back to 1.
    // Use String() to avoid type-mismatch (backend may send number IDs).
    if (activity?.activity_type === 'pdf_slideshow') {
      if (!lastPdfId || String(activity.id) !== String(lastPdfId)) {
        const slide = activity.activity_data?.current_slide ?? 1
        set({ currentSlide: slide, _lastPdfActivityId: String(activity.id) })
      }
    } else if (activity && activity.activity_type !== 'pdf_slideshow' && lastPdfId) {
      // Switched to a non-PDF activity — clear tracking
      set({ _lastPdfActivityId: null })
    }
    set({ currentActivity: activity })

    // Clear responses when no activity is selected
    if (!activity) {
      set({ responses: [] })
    }
  },

  updateActivityInLesson: (updatedActivity: LessonActivity) => {
    set((state) => {
      if (!state.currentLesson?.activities) return {}

      return {
        currentLesson: {
          ...state.currentLesson,
          activities: state.currentLesson.activities.map(activity =>
            activity.id === updatedActivity.id ? updatedActivity : activity
          )
        }
      }
    })
  },

  addResponse: (response: PlayerResponse) => {
    set((state) => ({
      responses: [...state.responses.filter(r => r.id !== response.id), response]
    }))
  },

  setResponses: (responses: PlayerResponse[]) => {
    set({ responses })
  },

  setConnectionStatus: (connected: boolean, error?: string) => {
    set({
      isConnected: connected,
      connectionError: error || null
    })
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  setAudioEnabled: (enabled: boolean) => {
    localStorage.setItem('audioEnabled', String(enabled))
    audioService.setEnabled(enabled)
    set({ audioEnabled: enabled })
  },

  connectToLesson: (lessonId: string, userRole?: 'host' | 'player', sessionInfo?: { runCode: string; sessionId: string }) => {
    let unsubscribe: () => void

    // Set connected status optimistically for UI responsiveness
    set({ isConnected: true, connectionError: null })

    if (sessionInfo && sessionInfo.sessionId) {
      // Ensure WebSocket is initialized (may have failed before AnyCable was running)
      if (!webSocketService.isConnected()) {
        webSocketService.reinitialize()
      }
      // Guest player connection
      unsubscribe = webSocketService.subscribeToGuestLesson(
        sessionInfo.runCode,
        sessionInfo.sessionId,
        {
          onLessonStateUpdate: (state) => {
            if (state.lesson) {
              get().updateLessonState(state.lesson)
            }
            if (state.current_activity !== undefined) {
              // Handle both null (no activity) and activity objects
              get().setCurrentActivity(state.current_activity)
            }
            if (state.players) {
              get().setPlayers(state.players)
            }
            if (state.slide_changed) {
              get().setCurrentSlide(state.slide_changed.page)
            }
          },

          onPlayerStatusChange: (statusData) => {
            if (statusData.player_id) {
              get().updatePlayer(statusData.player_id, {
                connection_status: statusData.status
              })
            }
          },

          onPlayerDataUpdate: (playerData) => {
            // Update the player data in the players list
            if (playerData.id) {
              get().updatePlayer(playerData.id, playerData)
            }
          },

          onError: (error) => {
            get().setConnectionStatus(false, error.message)
          }
        }
      )
    } else if (userRole === 'host') {
      // Lesson host connection
      unsubscribe = webSocketService.subscribeToLessonHost(lessonId, {
        onHostLessonState: (state) => {
          if (state.lesson) {
            get().updateLessonState(state.lesson)
            // current_activity is nested inside lesson in HostLessonStateSerializer
            if ('current_activity' in state.lesson) {
              get().setCurrentActivity(state.lesson.current_activity ?? null)
            }
          }
          // Sync the full activities list so statuses stay up to date
          if (state.activities && get().currentLesson) {
            set((s) => ({
              currentLesson: s.currentLesson
                ? { ...s.currentLesson, activities: state.activities }
                : null
            }))
          }
          if (state.players) {
            get().setPlayers(state.players)
          }
        },

        onLessonLifecycleEvent: (event) => {
          switch (event.type) {
            case 'lesson_started':
              get().updateLessonState({ status: 'live' })
              break
            case 'lesson_paused':
              get().updateLessonState({ status: 'paused' })
              break
            case 'lesson_resumed':
              get().updateLessonState({ status: 'live' })
              break
            case 'lesson_completed':
              get().updateLessonState({ status: 'completed' })
              break
          }
        },

        onActivityEvent: (event) => {
          // Handle both old format (event.data.activity) and new format (event.activity)
          const activity = event.data?.activity || event.activity

          // Handle auto-completion events
          if (event.type === 'host_activity_auto_completed') {
            audioService.play('all-responded')
            // Show toast notification
            toast.success('Activity completed - all players responded!', {
              duration: 4000,
              position: 'top-center',
            })

            get().setCurrentActivity(null)
            if (activity) {
              get().updateActivityInLesson(activity)
            }
          }
          // For other completed activities, set to null to show "waiting for activity"
          else if (event.type === 'activity_completed' || event.type === 'host_activity_completed') {
            get().setCurrentActivity(null)
            get().setResponses([]) // Clear responses when activity completes
            // Still update the activity in the list to show completed status
            if (activity) {
              get().updateActivityInLesson(activity)
            }
          } else if (activity) {
            get().setCurrentActivity(activity)
            // Update the activity in the lesson's activities list
            get().updateActivityInLesson(activity)
          }
        },

        onSlideChanged: (data) => {
          if (data.page) {
            get().setCurrentSlide(data.page)
          }
        },

        onRealTimeAnalytics: (_analytics) => {
          // Could store analytics in the store if needed
        },

        onPlayerResponse: (data) => {
          // Handle the response data format from the WebSocket
          const response = data.response || data
          if (response) {
            get().addResponse(response)
            audioService.play('response-received')
          }
        },

        onPlayerJoined: (playerData) => {
          get().addPlayer(playerData.player || playerData)
          audioService.play('player-joined')

          // Update player count
          get().updateLessonState({
            player_count: get().players.length + 1
          })
        },

        onPlayerLeft: (playerData) => {
          get().removePlayer(playerData.player_id || playerData.id)

          // Update player count
          get().updateLessonState({
            player_count: Math.max(0, get().players.length - 1)
          })
        },

        onError: (error) => {
          get().setConnectionStatus(false, error.message)
        }
      })
    } else {
      // Authenticated player connection
      unsubscribe = webSocketService.subscribeToLesson(lessonId, {
        onLessonStateChange: (state) => {
          if (state.lesson) {
            get().updateLessonState(state.lesson)
          }
          if (state.current_activity) {
            get().setCurrentActivity(state.current_activity)
          }
          if (state.players) {
            get().setPlayers(state.players)
          }
        },

        onPlayerJoined: (playerData) => {
          get().addPlayer(playerData.player || playerData)

          // Update player count
          get().updateLessonState({
            player_count: get().players.length + 1
          })
        },

        onPlayerLeft: (playerData) => {
          get().removePlayer(playerData.player_id || playerData.id)

          // Update player count
          get().updateLessonState({
            player_count: Math.max(0, get().players.length - 1)
          })
        },

        onActivityStarted: (activityData) => {
          const activity = activityData?.activity || activityData
          get().setCurrentActivity(activity) // Handle both activity objects and null
          if (activity) {
            get().setResponses([]) // Clear previous responses only when starting new activity
            get().updateActivityInLesson(activity) // Update activity in the lesson's activities list
          }
        },

        onResponseSubmitted: (responseData) => {
          get().addResponse(responseData.response || responseData)
        },

        onError: (error) => {
          get().setConnectionStatus(false, error.message)
        },
      })
    }

    // WebSocket connection status will be updated by callbacks
    // Keep optimistic connection status if WebSocket fails

    return unsubscribe
  },

  disconnectFromLesson: () => {
    const { currentLesson } = get()
    if (currentLesson) {
      webSocketService.unsubscribeFromLesson(currentLesson.id)
    }
    // Unsubscribe from all WebSocket connections to be safe
    webSocketService.unsubscribeAll()
    set({ isConnected: false, connectionError: null })
  },

  reset: () => {
    set({
      currentLesson: null,
      lessonSession: null,
      isConnected: false,
      connectionError: null,
      players: [],
      currentActivity: null,
      currentSlide: 1,
      _lastPdfActivityId: null,
      _suppressActivityPollUntil: 0,
      responses: [],
      isLoading: false,
      error: null,
    })
  },
}))

// Initialize audioService enabled state from localStorage on load
audioService.setEnabled(localStorage.getItem('audioEnabled') !== 'false')

